import React, { ChangeEvent, InputHTMLAttributes, forwardRef } from 'react'
import type { Ref } from 'react'
import { isEmpty } from 'lodash'
import clsx from 'clsx'

import { getLabel } from 'helpers/language'
import { IconCheckCircle, IconCloseCircle } from '../Icons'
import Loader from '../Loader'

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  variants?: 'primary' | 'secondary'
  /**
   * What success to perform ?
   */
  isSuccess?: boolean
  /**
   * What error to perform ?
   */
  isError?: boolean
  /**
   * What isLoading to perform ?
   */
  isLoading?: boolean
  /**
   * What showIcon to perform ?
   */
  showIcon?: boolean
  /**
   * What message to perform ?
   */
  message?: string
  /**
   * Where non border of position to perform ?
   */
  group?: 'left' | 'right'
  /**
   * Optional resize width
   */
  fullWidth?: boolean
  /**
   * What icon to perform ?
   */
  withIcon?: React.ReactNode
  /**
   * Optional change handler
   */
  editBranchWDropdown?: boolean
  isWarranty?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  value?: string
  showDropdown?: boolean
}

/**
 * Primary UI component for user interaction
 */

function TextInput(
  props: TextInputProps,
  ref: Ref<HTMLInputElement>,
): JSX.Element {
  const {
    type = 'text',
    variants = 'primary',
    className,
    isError = false,
    isSuccess = false,
    isLoading = false,
    disabled = false,
    showIcon = false,
    fullWidth = false,
    group,
    message,
    withIcon,
    value,
    editBranchWDropdown,
    isWarranty,
    showDropdown = false,
    ...others
  } = props

  const hasIcon = typeof withIcon === 'object' && !isEmpty(withIcon)
  const hasStatus = isError || isSuccess || isLoading
  const showIconByStatus = isLoading ? (
    <Loader variants='primary' />
  ) : isError ? (
    <IconCloseCircle />
  ) : (
    <IconCheckCircle />
  )
  const cn = clsx(
    'text-input',
    `text-input--${variants}`,
    isError && !showDropdown && 'text-input--danger',
    isSuccess && 'text-input--success',
    disabled && 'text-input--disabled',
    group && `text-input--${group}-group`,
    isWarranty && 'text-input--isWarranty',
    editBranchWDropdown && 'text-input--editBranchWDropdown',
    className,
  )

  const textInputProps = {
    ...others,
    value,
    type,
    className: cn,
  }

  return (
    <div
      className={clsx(
        'text-input__wrapper',
        fullWidth && 'text-input__wrapper--full-width',
      )}
      data-testid='text-input'
    >
      <div
        className={clsx(
          'text-input__group',
          disabled && 'text-input__group--disabled',
        )}
        data-testid='text-input-container'
      >
        <input
          {...textInputProps}
          disabled={disabled}
          ref={ref}
          data-testid='text-input-element'
          autoComplete='off'
        />
        {(hasIcon || hasStatus) && (
          <span
            className={clsx('text-input__icon', {
              'text-input__icon--error': showIcon && isError && !showDropdown,
              'text-input__icon--success': showIcon && isSuccess,
            })}
            data-testid='text-input-icon'
          >
            {showIcon && hasStatus ? showIconByStatus : withIcon}
          </span>
        )}
      </div>
      {message && !showDropdown && (
        <span
          className={clsx('text-input__message', {
            'text-input__error': isError,
            'text-input__success': isSuccess,
          })}
          data-testid='text-input-message'
        >
          {getLabel(message)}
        </span>
      )}
    </div>
  )
}

export default forwardRef(TextInput)
